import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Search,
  Plus,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  Package,
  User,
  Calendar,
  FileText,
  AlertCircle,
} from "lucide-react";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";
import {
  server_post_data,
  get_kitchen_request_list,
} from "../ServiceConnection/serviceconnection";

const statusConfig = {
  0: {
    label: "Pending",
    color: "bg-warning/20 text-warning border-warning/30",
    icon: Clock,
  },
  1: {
    label: "Approved",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle2,
  },
  2: {
    label: "Converted to PO",
    color: "bg-primary/20 text-primary border-primary/30",
    icon: Eye,
  },
  3: {
    label: "Rejected",
    color: "bg-destructive/20 text-destructive border-destructive/30",
    icon: XCircle,
  },
};

const priorityConfig = {
  high: { label: "High", color: "bg-destructive/20 text-destructive" },
  medium: { label: "Medium", color: "bg-warning/20 text-warning" },
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
};

export default function Indents() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({
    pending: 0,
    approved: 0,
    converted: 0,
    rejected: 0,
  });
  const [indents, setIndents] = useState([]);

  // View Dialog State
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingIndent, setViewingIndent] = useState(null);

  const fetchIndents = useCallback(async () => {
    setLoading(true);
    const fd = new FormData();
    fd.append("status", statusFilter === "all" ? "" : statusFilter);
    fd.append("search", searchQuery);

    try {
      const response = await server_post_data(get_kitchen_request_list, fd);
      if (!response.data.error && response.data.message) {
        setDashboard(response.data.message.dashboard);
        console.log("Fetched indents:", response.data.message.list);
        setIndents(response.data.message.list);
      } else {
        setIndents([]);
        setDashboard({ pending: 0, approved: 0, converted: 0, rejected: 0 });
      }
    } catch (error) {
      console.error("Error fetching indents:", error);
      setIndents([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchIndents();
  }, [fetchIndents]);

  // Re-fetch on filter change
  useEffect(() => {
    const timer = setTimeout(() => fetchIndents(), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter]);

  const getStatusIcon = (status) => {
    const config = statusConfig[status];
    const Icon = config?.icon || Clock;
    return <Icon className="w-4 h-4" />;
  };

  // Handle view indent
  const handleViewClick = useCallback((indent) => {
    setViewingIndent(indent);
    setIsViewDialogOpen(true);
  }, []);

  return (
    <MainLayout>
      <Header
        title="Indent Management"
        subtitle="Department-wise purchase requests"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.pending}
                </p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.approved}
                </p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.converted}
                </p>
                <p className="text-sm text-muted-foreground">Converted</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {dashboard.rejected}
                </p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by indent no or item..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Link to="/create-indent">
            <Button className="gap-2 gradient-primary text-primary-foreground hover:opacity-90">
              <Plus className="w-4 h-4" /> New Indent
            </Button>
          </Link>
        </div>

        {/* Indents Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading indents...
            </div>
          ) : indents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No indents found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">
                    Indent No
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  {/* <TableHead className="text-muted-foreground">Item</TableHead>
                  <TableHead className="text-muted-foreground">Qty</TableHead> */}
                  <TableHead className="text-muted-foreground">
                    Priority
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Requested By
                  </TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {indents.map((indent) => (
                  <TableRow
                    key={indent.request_id}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium text-primary">
                      {indent.indent_no}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {indent.department}
                      </Badge>
                    </TableCell>
                    {/* <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium text-foreground">
                          {indent.items[0]?.ingredient_name || "No items"}
                          {indent.item_count > 1 && (
                            <span className="text-xs text-muted-foreground ml-2">
                              + {indent.item_count - 1} more
                            </span>
                          )}
                        </div>
                        {indent.remarks && (
                          <p className="text-xs text-muted-foreground max-w-xs truncate">
                            {indent.remarks}
                          </p>
                        )}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <div>
                          {indent.items[0]?.requested_qty}{" "}
                          {indent.items[0]?.unit_name}
                        </div>
                        {indent.item_count > 1 && (
                          <div className="text-xs text-muted-foreground">
                            + {indent.item_count - 1} item
                            {indent.item_count > 2 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    </TableCell> */}
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-medium border-0",
                          priorityConfig[indent.priority?.toLowerCase()]
                            ?.color || "bg-muted"
                        )}
                      >
                        {priorityConfig[indent.priority?.toLowerCase()]
                          ?.label || indent.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={cn(
                          "font-medium border gap-1",
                          statusConfig[indent.status]?.color
                        )}
                      >
                        {getStatusIcon(indent.status)}
                        {statusConfig[indent.status]?.label || "Unknown"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {indent.requested_by}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(indent.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewClick(indent)}
                        className="hover:bg-primary/10 hover:text-primary"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* View Indent Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Indent Details</DialogTitle>
              <DialogDescription>
                Complete information about this indent request
              </DialogDescription>
            </DialogHeader>

            {viewingIndent && (
              <div className="space-y-6 py-4">
                {/* Header Card */}
                <div className="flex items-start justify-between p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center">
                      <FileText className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {viewingIndent.indent_no}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Request ID: {viewingIndent.request_id}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Badge
                      className={cn(
                        "font-medium border gap-1",
                        statusConfig[viewingIndent.status]?.color
                      )}
                    >
                      {getStatusIcon(viewingIndent.status)}
                      {statusConfig[viewingIndent.status]?.label || "Unknown"}
                    </Badge>
                    <Badge
                      className={cn(
                        "font-medium border-0",
                        priorityConfig[viewingIndent.priority?.toLowerCase()]
                          ?.color || "bg-muted"
                      )}
                    >
                      {priorityConfig[viewingIndent.priority?.toLowerCase()]
                        ?.label || viewingIndent.priority}{" "}
                      Priority
                    </Badge>
                  </div>
                </div>

                {/* Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Department */}
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Department
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingIndent.department}
                    </p>
                  </div>

                  {/* Requested By */}
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Requested By
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {viewingIndent.requested_by}
                    </p>
                  </div>

                  {/* Date */}
                  <div className="p-4 border rounded-lg bg-card">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-primary" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Request Date
                      </p>
                    </div>
                    <p className="text-base font-semibold text-foreground ml-10">
                      {new Date(viewingIndent.date).toLocaleDateString(
                        "en-US",
                        {
                          weekday: "short",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>

                  {/* Category (if available) */}
                  {viewingIndent.category && (
                    <div className="p-4 border rounded-lg bg-card">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Package className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          Category
                        </p>
                      </div>
                      <p className="text-base font-semibold text-foreground ml-10">
                        {viewingIndent.category}
                      </p>
                    </div>
                  )}
                </div>

                {/* Item Details Section */}
                {/* Item Details Section - Multiple Items */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Item Details ({viewingIndent.item_count} item
                    {viewingIndent.item_count !== 1 ? "s" : ""})
                  </h4>

                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="text-muted-foreground">
                            Item Name
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Quantity
                          </TableHead>
                          <TableHead className="text-muted-foreground text-center">
                            Unit
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingIndent.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium py-3">
                              <div className="flex flex-col gap-0.5">
                                {/* 1. Main Name: Asli naam pehle dikhega */}
                                <span className="text-sm text-foreground">
                                  {item.ingredient_name}
                                </span>

                                {/* 2. Alter Name: Niche bracket mein chota font */}
                                {item.ingredient_alter_name && 
                                item.ingredient_alter_name.trim().toLowerCase() !== (item.ingredient_name)?.trim().toLowerCase() && (
                                  <span className="text-[11px] text-muted-foreground font-normal leading-none">
                                    ({item.ingredient_alter_name})
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              {item.requested_qty}
                            </TableCell>
                            <TableCell className="text-center text-muted-foreground">
                              {item.unit_name}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Remarks Section */}
                {viewingIndent.remarks && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Remarks
                    </h4>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <p className="text-sm text-foreground">
                        {viewingIndent.remarks}
                      </p>
                    </div>
                  </div>
                )}

                {/* Additional Info (if needed) */}
                {viewingIndent.approved_by && (
                  <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircle2 className="w-5 h-5" />
                      <div>
                        <p className="font-medium">Approved by</p>
                        <p className="text-sm">{viewingIndent.approved_by}</p>
                      </div>
                    </div>
                  </div>
                )}

                {viewingIndent.rejected_by && (
                  <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-center gap-2 text-destructive">
                      <XCircle className="w-5 h-5" />
                      <div>
                        <p className="font-medium">Rejected by</p>
                        <p className="text-sm">{viewingIndent.rejected_by}</p>
                        {viewingIndent.rejection_reason && (
                          <p className="text-xs mt-1">
                            Reason: {viewingIndent.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
